import {FIELDSET_LAYOUT} from 'components/customer/Product/FieldSet';
import {SizePercentage} from 'components/customer/Product/Specs/SizePercentage';
import {useAppContext} from 'contexts/ApplicationContext';
import {useProductContext} from 'contexts/ProductContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useField, useFormikContext} from 'formik';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Col, Form, Row} from 'react-bootstrap';
import {
    getCabinetTop,
    getHardwareMethods,
    getAdjustableLegsMethods,
    getSupplyMethods,
} from 'service/RoomService';
import {Appliance} from 'shared/components/Forms/Applicance';
import {Boolean} from 'shared/components/Forms/Boolean';
import {DropdownDecorator} from 'shared/components/Forms/DropdownDecorator';
import {FormControl} from 'shared/components/Forms/FormControl';
import {Number} from 'shared/components/Forms/Number';
import {Size} from 'shared/components/Forms/Size';
import {MoreInfo} from 'shared/components/MoreInfo/MoreInfo';
import {Radio} from 'shared/components/Forms/Radio';
import {
    DynamicRadio,
    getIncludeDoorFacesImage,
    getIncludeDoorFacesLabel,
} from 'shared/components/Forms/DynamicRadio';
import excel from 'shared/Excel';
import {
    randomString,
    getFieldValue,
    hasOption,
    isOptionBoolean,
    isFieldEnabled,
    Icon,
} from 'shared/helpers';
import {EdgeFinishes} from 'components/customer/EdgeFinishes/EdgeFinishes';
import {validationErrorAppliesToField} from 'shared/validator';
import {isDeviceSmall, useTabletSize} from 'shared/helpers/DeviceSize';
import styled from 'styled-components';
import {useGetDrawerSystemQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {useGetHingeStyle} from 'components/customer/Product/helpers/useGetHingeStyle';
import {useLazyGetFingerPullStylesQuery} from 'components/customer/Product/store/productApi';
import {cloneDeep} from 'lodash';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {ShelfThickness} from 'components/customer/Product/Shelves/ShelfThickness';
import {useGetDoorQuery} from 'components/customer/Materials/store/materialApi';

export const FIELD_TYPES = {
    NUMERIC: 'numeric',
    SIZE: 'size',
    TEXT: 'text',
    PART_TOP: 'part_top',
    SUPPLY_METHOD: 'assemblymethod',
    HARDWARE_METHOD: 'hardwaremethod',
    INCLUDE_DOOR_FACES: 'includedoorfaces',
    ADJUSTABLE_LEGS: 'adjustablelegs',
    MATERIAL: 'material',
    LABEL: 'label',
    SIZE_LABEL: 'size_label',
    BOOLEAN: 'boolean',
    DOOR_HANG: 'door_hang',
    SELECT: 'select',
    HINGE_STYLE: 'hinge_style',
    EDGE_FINISH: 'edge_finish',
    SHELF_TYPES: 'simple_shelf_type',
    SIZE_PERCENTAGE: 'size_percentage',
    FINGER_PULL: 'fingerpull',
    APPLIANCE: 'appliance',
    RADIO: 'radio',
    SHELVES_THICKNESS: 'simple_shelves_thickness',
};

export const CABINET_TYPES = {
    SHAPE_TYPE_ID: 9,
    STYLE_TALL: 2,
    SUB_STYLE_TALL_RETURN_BLIND: 6,
    STYLE_PANTRY: 4,
    SUB_STYLE_PANTRY_RETURN_BLIND: 38,
};

const ButtonSlotWrapper = styled.div`
    display: flex;
    gap: 12px;
    & > div {
        width: 50%;
        padding: 0 !important;
        &.input-group {
            align-items: baseline;
        }
    }
`;

const isFieldVisible = (field, values, materialOptions, fieldSetIndex = '') => {
    try {
        let visible = field.options.visible;
        if (typeof visible === 'string' && visible?.includes('{index}')) {
            visible = visible.replace('{index}', `${fieldSetIndex + 1}`);
        }

        return excel.calculate(visible, values);
    } catch (e) {
        if (field.options.visible === 'cabinet_door.advanced') {
            return materialOptions?.cabinet_door?.advanced;
        } else return false;
    }
};

export const getVisibility = (
    field,
    fieldValue,
    values,
    indexedValues,
    materialOptions,
    fieldSetIndex = ''
) => {
    let visible = true;

    if (hasOption(field, 'visible')) {
        const controlledVisibility = field.options.visible;

        if (typeof controlledVisibility === 'function') {
            visible = controlledVisibility(values, fieldValue, indexedValues);
        } else {
            if (isOptionBoolean(field, 'visible')) {
                visible = controlledVisibility;
            } else {
                try {
                    visible = isFieldVisible(
                        field,
                        values,
                        materialOptions,
                        fieldSetIndex
                    );
                } catch (e) {
                    //
                }
            }
        }
    }

    return visible;
};

const getDataSource = (type, displayName = null) => {
    const DataSource = {
        [FIELD_TYPES.PART_TOP]: async (params) => {
            return await getCabinetTop(params);
        },
        [FIELD_TYPES.SUPPLY_METHOD]: getSupplyMethods,
        [FIELD_TYPES.HARDWARE_METHOD]: getHardwareMethods,
        [FIELD_TYPES.ADJUSTABLE_LEGS]: getAdjustableLegsMethods,
        [FIELD_TYPES.HINGE_STYLE]: async (params) => {
            const {
                hingeStyles,
                include_hardware: includeHardware,
                isAssemblyOnly,
            } = params;

            if (hingeStyles) {
                return cloneDeep(hingeStyles)
                    .filter(
                        ({name}) =>
                            !isAssemblyOnly ||
                            !name?.toLowerCase().includes('drill only')
                    )
                    .map((hingeStyle) => {
                        if (
                            includeHardware == 1 &&
                            hingeStyle.name
                                ?.toLowerCase()
                                .includes('drill only')
                        ) {
                            hingeStyle.disabled = true;
                        }
                        return hingeStyle;
                    });
            }
        },
        [FIELD_TYPES.EDGE_FINISH]: ({edgeFinishes}) => {
            return edgeFinishes;
        },
        [FIELD_TYPES.FINGER_PULL]: async (params) => {
            const {
                defaultValuesReference: data,
                productDefaultsReference,
                fetchFingerPulStyles,
            } = params;
            if (fetchFingerPulStyles) {
                // Prefer product defaults (product data without cabinetId,
                // inheriting room defaults) for the "None" option's gap and
                // top margin values. When editing, saved data contains
                // handler-modified values (e.g. topMargin = 0 from a
                // recessed handle), but we need the original defaults so
                // "None" can reset correctly.
                const defaults =
                    productDefaultsReference?.current &&
                    Object.keys(productDefaultsReference.current).length > 0
                        ? productDefaultsReference.current
                        : (data?.current ?? {});
                const drawerGap = defaults.hasOwnProperty('cabinet_drawer_gap')
                    ? defaults.cabinet_drawer_gap
                    : defaults.cabinet_door_gap;
                const topMargin = defaults.hasOwnProperty('cabinet_drawer_top')
                    ? defaults.cabinet_drawer_top
                    : defaults.cabinet_door_top;

                const {data: fingerPullStyles} = await fetchFingerPulStyles(
                    {
                        drawerGap: drawerGap != null ? drawerGap : 0,
                        topMargin: topMargin != null ? topMargin : 0,
                    },
                    true
                );

                return fingerPullStyles;
            }
        },
        [FIELD_TYPES.INCLUDE_DOOR_FACES]: () => {
            const options = getIncludeDoorFacesImage(displayName);
            return [
                {
                    doorFaces: true,
                    value: 0,
                    name: `No ${getIncludeDoorFacesLabel(displayName)} Faces`,
                    description: '',
                    image: (
                        <Icon
                            iconName={options.noOptionImage}
                            className="icon-thumbnail"
                        />
                    ),
                    id: 0,
                },
                {
                    doorFaces: true,
                    value: 1,
                    name: `With ${getIncludeDoorFacesLabel(displayName)} Faces`,
                    description: '',
                    image: (
                        <Icon
                            iconName={options.yesOptionImage}
                            className="icon-thumbnail"
                        />
                    ),
                    id: 1,
                },
            ];
        },
    };

    return DataSource[String(type)];
};

const formatSelectOptions = (options, values) => {
    if (Array.isArray(options)) {
        return options
            .filter((option) => {
                if (
                    typeof option === 'object' &&
                    option.hasOwnProperty('condition')
                ) {
                    try {
                        return excel.calculate(option.condition, values);
                    } catch (e) {
                        return true;
                    }
                }

                return true;
            })
            .map((option) => {
                if (typeof option !== 'object') {
                    return {id: option, name: option};
                } else {
                    return {
                        id: isNaN(option.value)
                            ? option.value
                            : parseInt(option.value),
                        name: option.label,
                    };
                }
            });
    } else {
        const formattedOptions = [];
        Object.keys(options).forEach((key) => {
            formattedOptions.push({
                id: parseInt(key),
                name: options[String(key)],
            });
        });

        return formattedOptions;
    }
};

const Dropdown = {
    render({
        field,
        setFieldValue,
        selectHandler,
        dataSource,
        dataSourceParams,
        values,
        enabled,
        isQFP,
        ...props
    }) {
        if (
            field.hasOwnProperty('options') &&
            field.options.hasOwnProperty('selectOptions')
        ) {
            if (field.options.hasOwnProperty('isQFP') && field.options.isQFP) {
                dataSource = field.options.selectOptions;
            } else
                dataSource = formatSelectOptions(
                    field.options.selectOptions,
                    values
                );
        }

        let hasImage = false;
        let hasDescription = false;
        if (field.type === FIELD_TYPES.FINGER_PULL) {
            hasImage = 'imageUrl';
            hasDescription = 'description';
        }

        if (
            field.type === FIELD_TYPES.SUPPLY_METHOD ||
            field.type === FIELD_TYPES.HARDWARE_METHOD ||
            field.type === FIELD_TYPES.ADJUSTABLE_LEGS
        ) {
            hasDescription = 'description';
        }

        return (
            <DropdownDecorator
                setFieldValue={setFieldValue}
                selectHandler={selectHandler}
                dataSource={dataSource ? dataSource : getDataSource(field.type)}
                dataSourceParams={dataSourceParams}
                enabled={enabled}
                hasImage={hasImage}
                hasDescription={hasDescription}
                field={field}
                isQFP={isQFP}
                {...props}
            />
        );
    },
};

const Label = {
    render({value, style, ...props}) {
        if (typeof style === 'undefined') return value;

        switch (style) {
            case 'primary': // Styles that the "Alert" component handles
            case 'secondary':
            case 'success':
            case 'danger':
            case 'warning':
            case 'info':
            case 'dark':
            case 'light':
                return <Alert variant={style}>{value}</Alert>;
            case 'form_item':
                return <Form.Label style={{margin: 0}}>{value}</Form.Label>;
            case 'underneath_field':
                return (
                    <section
                        style={{
                            fontSize: '.75em',
                            textAlign: 'justify',
                        }}>
                        {value}
                    </section>
                );
        }

        return value;
    },
};

const DynamicRadioRender = {
    render({
        name,
        value,
        field,
        setFieldValue,
        selectHandler,
        extraHandler,
        isQFP,
        enabled,
        ...props
    }) {
        value = value > 0;

        if (isQFP) {
            setFieldValue = selectHandler;
        }

        const data = getDataSource(field.type, field.displayName);
        const hideYesOption = field?.options?.hideYesOption;
        const hideNoOption = field?.options?.hideNoOption;

        return (
            <DynamicRadio
                name={name}
                value={value}
                field={field}
                setFieldValue={setFieldValue}
                disabled={!enabled}
                options={data}
                isQFP={isQFP}
                yesOptionHidden={hideYesOption}
                noOptionHidden={hideNoOption}
            />
        );
    },
};

const DynamicSizeRender = {
    render({
        field,
        setFieldValue,
        dataSource,
        dataSourceParams,
        values,
        enabled,
        extraHandler,
        ...props
    }) {
        const withDeleteIcon =
            field?.options &&
            field?.options.hasOwnProperty('hasDeleteIcon') &&
            field?.options.hasDeleteIcon;

        if (
            props?.name == 'shelf_offset' &&
            Array.isArray(values?.shelves) &&
            values.shelves[props.fieldSetIndex] &&
            values.shelves[props.fieldSetIndex].shelf_type ==
                CABINET_TYPES.SHAPE_TYPE_ID && // Shape
            props?.productDataStore?.current?.cabinet?.attributes?.style ==
                CABINET_TYPES.STYLE_TALL && // Tall
            props?.productDataStore?.current?.cabinet?.attributes
                ?.sub_style_id == CABINET_TYPES.SUB_STYLE_TALL_RETURN_BLIND // Tall Return Blind
        ) {
            props.value = 0;
        }

        if (
            props?.name == 'upper_shelf_offset' &&
            Array.isArray(values?.upper_shelves) &&
            values.upper_shelves[props.fieldSetIndex] &&
            props?.productDataStore?.current?.cabinet?.attributes?.style ==
                CABINET_TYPES.STYLE_PANTRY && // Pantry
            props?.productDataStore?.current?.cabinet?.attributes
                ?.sub_style_id == CABINET_TYPES.SUB_STYLE_PANTRY_RETURN_BLIND // Pantry Return Blind
        ) {
            const shelves = values.upper_shelves[props.fieldSetIndex];

            props.value =
                shelves.upper_shelf_type == CABINET_TYPES.SHAPE_TYPE_ID // Shape
                    ? 0
                    : props.value != shelves.shelf_offset
                      ? props.value
                      : shelves.shelf_offset || 0;
        }

        if (
            props?.name == 'lower_shelf_offset' &&
            Array.isArray(values?.lower_shelves) &&
            values.lower_shelves[props.fieldSetIndex] &&
            props?.productDataStore?.current?.cabinet?.attributes?.style ==
                CABINET_TYPES.STYLE_PANTRY && // Pantry
            props?.productDataStore?.current?.cabinet?.attributes
                ?.sub_style_id == CABINET_TYPES.SUB_STYLE_PANTRY_RETURN_BLIND // Pantry Return Blind
        ) {
            const shelves = values.lower_shelves[props.fieldSetIndex];

            props.value =
                shelves.lower_shelf_type == CABINET_TYPES.SHAPE_TYPE_ID
                    ? 0
                    : props.value != shelves.shelf_offset
                      ? props.value
                      : shelves.shelf_offset || 0;
        }

        return (
            <Size
                {...{field, ...props}}
                disabled={!enabled}
                metric="mm"
                inSpecsTab={props.inSpecsTab}
                withDeleteIcon={withDeleteIcon}
                deleteAction={field?.options?.deleteAction}
                customWidth={field?.options?.customWidth}
            />
        );
    },
};

const FieldStrategy = {
    [FIELD_TYPES.NUMERIC]: {
        render({
            field,
            setFieldValue,
            dataSource,
            dataSourceParams,
            values,
            enabled,
            extraHandler,
            isQFP,
            ...props
        }) {
            let min = 0;
            let max = 9999;

            if (
                field.hasOwnProperty('options') &&
                field.options.hasOwnProperty('maximum')
            ) {
                max = field.options.maximum;
                if (typeof max === 'string') {
                    max = excel.calculate(max, values);
                }
            }

            if (
                field.hasOwnProperty('options') &&
                field.options.hasOwnProperty('minimum')
            ) {
                min = field.options.minimum;
            }

            return (
                <Number
                    min={min}
                    max={max}
                    {...props}
                    in_sizes_tab={props.inSpecsTab}
                />
            );
        },
    },
    [FIELD_TYPES.SIZE]: DynamicSizeRender,
    [FIELD_TYPES.TEXT]: {
        render({
            field,
            setFieldValue,
            dataSource,
            dataSourceParams,
            values,
            enabled,
            extraHandler,
            showDialog,
            hideDialog,
            setCurrentTab,
            formStructure,
            value,
            ...props
        }) {
            let fieldValue = value;
            if (value === 0 && !field.value) fieldValue = '';

            return (
                <FormControl
                    as={
                        field.options.hasOwnProperty('multiLine') &&
                        field.options.multiLine
                            ? 'textarea'
                            : 'input'
                    }
                    rows={
                        field.options.hasOwnProperty('multiLine') &&
                        field.options.multiLine
                            ? 5
                            : 1
                    }
                    type="text"
                    disabled={!enabled}
                    maxLength={
                        field.options.hasOwnProperty('maximum')
                            ? field.options.maximum
                            : ''
                    }
                    value={fieldValue}
                    {...props}
                />
            );
        },
    },
    [FIELD_TYPES.LABEL]: Label,
    [FIELD_TYPES.SIZE_LABEL]: {
        render({
            field,
            setFieldValue,
            selectHandler,
            dataSource,
            dataSourceParams,
            values,
            enabled,
            extraHandler,
            isQFP,
            ...props
        }) {
            return (
                <Size
                    disabled={true}
                    {...props}
                    metric="mm"
                    in_sizes_tab={props.inSpecsTab}
                />
            );
        },
    },
    [FIELD_TYPES.BOOLEAN]: {
        render({
            name,
            value,
            field,
            setFieldValue,
            selectHandler,
            extraHandler,
            isQFP,
            ...props
        }) {
            const type = 'checkbox';

            // detect whether it is select box as well
            value = value > 0;

            if (isQFP) {
                setFieldValue = selectHandler;
            }

            // eslint-disable-next-line require-jsdoc -- dont really needs a doc
            const handleFieldValue = (name_, value_) => {
                if (name === 'cabinet_simple_shelves') {
                    setFieldValue(name_, !value_);
                } else {
                    setFieldValue(name_, value_);
                }
            };

            // unique condition for cabinet_simple_shelves
            return (
                <Boolean
                    name={name}
                    value={name === 'cabinet_simple_shelves' ? !value : value}
                    field={field}
                    // eslint-disable-next-line react/jsx-no-bind -- do proper fix later when this is migrated to tsx
                    setFieldValue={handleFieldValue}
                    type={type}
                />
            );
        },
    },
    [FIELD_TYPES.RADIO]: {
        render({
            name,
            value,
            field,
            setFieldValue,
            selectHandler,
            extraHandler,
            isQFP,
            ...props
        }) {
            // detect whether it is select box as well
            value = value > 0;

            if (isQFP) {
                setFieldValue = selectHandler;
            }

            const handleSelect = (name_, value_) => {
                setFieldValue(name_, value_);
            };

            return (
                <Radio
                    name={name}
                    value={value}
                    field={field}
                    // eslint-disable-next-line react/jsx-no-bind -- do proper fix later when this is migrated to tsx
                    setFieldValue={handleSelect}
                />
            );
        },
    },
    [FIELD_TYPES.SIZE_PERCENTAGE]: {
        render({
            field,
            fieldsetName,
            setFieldValue,
            selectHandler,
            dataSource,
            dataSourceParams,
            values,
            enabled,
            extraHandler,
            isQFP,
            ...props
        }) {
            return (
                <SizePercentage
                    selectHandler={
                        selectHandler ? selectHandler : setFieldValue
                    }
                    {...props}
                    disabled={!enabled}
                    fieldsetName={fieldsetName}
                    metric="mm"
                />
            );
        },
    },
    [FIELD_TYPES.PART_TOP]: Dropdown,
    [FIELD_TYPES.INCLUDE_DOOR_FACES]: DynamicRadioRender,
    [FIELD_TYPES.SUPPLY_METHOD]: DynamicRadioRender,
    [FIELD_TYPES.ADJUSTABLE_LEGS]: DynamicRadioRender,
    [FIELD_TYPES.HARDWARE_METHOD]: {
        render(props) {
            const hasDrillOnlyDrawer =
                Array.isArray(props.values?.drawers) &&
                props.values.drawers.some((drawer) => {
                    const drawerType = drawer?.drawer_type;
                    const selectedDrawerSystem = props.drawerSystems?.find(
                        (drawerSystem) => drawerSystem.id == drawerType
                    );

                    return !!selectedDrawerSystem?.drillOnly;
                });

            const selectedHingeStyle = props.hingeStyles?.find(
                (hingeStyle) => hingeStyle.id == props.values?.hinge_style
            );

            const isDrillOnly =
                typeof selectedHingeStyle?.name === 'string' &&
                selectedHingeStyle.name.includes('drill only');

            const supplyHardwareDisabled = props?.field?.options?.hideYesOption;
            if (supplyHardwareDisabled) {
                // Force include hardware option to false/off (the option to
                // include hardware will not be rendered) ...
                props.setFieldValue('cabinet_include_hardware', 0);
            }

            const shouldShowDialog =
                props.values?.cabinet_include_hardware &&
                (hasDrillOnlyDrawer || isDrillOnly);

            if (shouldShowDialog) {
                const specificMessage = {
                    title: 'Option Unavailable',
                    text: '<p>The following are not available with your hardware inclusions selection of Supply Hardware.</p>',
                };

                if (hasDrillOnlyDrawer) {
                    props.values.drawers.forEach((drawer) => {
                        const drawerType = drawer?.drawer_type;
                        const selectedDrawerSystem = props.drawerSystems?.find(
                            (drawerSystem) => drawerSystem.id === drawerType
                        );

                        if (selectedDrawerSystem?.drillOnly) {
                            specificMessage.text += `<li>Drawer - ${selectedDrawerSystem.name}</li>`;
                        }
                    });
                }

                if (isDrillOnly) {
                    specificMessage.text += `<li>Hinge style - ${selectedHingeStyle.name}</li>`;
                }

                if (props.values?.cabinet_include_assembly) {
                    props.setFieldValue('cabinet_include_assembly', 0);
                }
                props.setFieldValue('cabinet_include_hardware', 0);
                props.showDialog({
                    title: specificMessage.title,
                    message: specificMessage.text,
                    hideYesButton: true,
                    hideNoButton: true,
                    buttons: [
                        {
                            name: 'Ok',
                            show: true,
                        },
                    ],
                });
            }

            return DynamicRadioRender.render(props);
        },
    },
    [FIELD_TYPES.MATERIAL]: Dropdown,
    [FIELD_TYPES.SELECT]: Dropdown,
    [FIELD_TYPES.HINGE_STYLE]: {
        render(props) {
            if (
                props.hasOwnProperty('selectHandler') &&
                typeof props.selectHandler == 'function'
            ) {
                props.ogHandler = props.selectHandler;
            } else {
                props.ogHandler = props.setFieldValue;
            }

            props.selectHandler = (name, value, options) => {
                const drillOnlyHinge =
                    options && options.find((hinge) => hinge.id == value);
                if (drillOnlyHinge) {
                    if (
                        drillOnlyHinge.name
                            .toLowerCase()
                            .indexOf('drill only') >= 0
                    ) {
                        props.ogHandler('drill_only_hinge', true);
                        props.ogHandler(
                            'drill_only_hinge_name',
                            drillOnlyHinge.name
                        );
                    } else {
                        props.ogHandler('drill_only_hinge', false);
                        props.ogHandler(
                            'drill_only_hinge_name',
                            drillOnlyHinge.name
                        );
                    }
                }

                props.ogHandler(name, value);
            };

            return Dropdown.render(props);
        },
    },
    [FIELD_TYPES.EDGE_FINISH]: {
        render({
            field,
            selectHandler,
            setFieldValue,
            isQFP,
            randomId,
            fieldIndex,
            fieldsetName,
            fieldSetIndex,
            value,
        }) {
            return (
                <EdgeFinishes
                    index={fieldIndex == -1 ? 0 : fieldIndex}
                    fieldId={randomId}
                    fieldName={field.name}
                    selectHandler={selectHandler}
                    setFieldValue={setFieldValue}
                    isQFP={isQFP}
                    fieldsetName={fieldsetName}
                    fieldSetIndex={fieldSetIndex}
                    value={value}
                />
            );
        },
    },
    [FIELD_TYPES.SHELF_TYPES]: Dropdown,
    [FIELD_TYPES.FINGER_PULL]: {
        render(props) {
            if (
                props.hasOwnProperty('selectHandler') &&
                typeof props.selectHandler == 'function'
            ) {
                props.ogHandler = props.selectHandler;
            } else {
                props.ogHandler = props.setFieldValue;
            }

            props.selectHandler = (name, value, options) => {
                if (value == -1 || !props.values.hasOwnProperty('drawers')) {
                    props.ogHandler(name, value, options);
                }
                if (
                    value != -1 &&
                    props.values.hasOwnProperty('drawers') &&
                    props.values.drawers.length > 0
                ) {
                    const filteredFormStructure = props.formStructure.filter(
                        (fs) =>
                            fs?.layout != 'sidebar' &&
                            (fs?.options?.visible ?? true)
                    );
                    const productConfig = structuredClone(
                        filteredFormStructure
                    );
                    const drawersTabIndex = productConfig.findIndex(
                        (config) => config.name == 'Drawers'
                    );
                    const doorsTabIndex = productConfig.findIndex(
                        (config) => config.name == 'Doors'
                    );

                    const hasDrawersTab = drawersTabIndex !== -1;
                    const targetTabIndex = hasDrawersTab
                        ? drawersTabIndex
                        : doorsTabIndex;
                    const targetTabName = hasDrawersTab ? 'Drawers' : 'Doors';

                    props.showDialog({
                        title: hasDrawersTab ? 'Check Drawers' : 'Check Doors',
                        message: hasDrawersTab
                            ? '<p>Selecting this finger pull style increases the gaps between the drawers.</p><p>Please re-check the drawer heights, runners before saving this product.</p>'
                            : '<p>Selecting this finger pull style affects the door margins.</p><p>Please re-check the door margins before saving this product.</p>',
                        hideYesButton: true,
                        hideNoButton: true,
                        buttons: [
                            {
                                name: 'Cancel',
                                show: true,
                            },
                            {
                                name: `Move to ${targetTabName}`,
                                show: true,
                                action: () => {
                                    props.ogHandler(name, value, options);
                                    if (targetTabIndex !== -1) {
                                        props.setCurrentTab(targetTabIndex);
                                    }
                                },
                            },
                        ],
                    });
                }
            };

            return Dropdown.render(props);
        },
    },
    [FIELD_TYPES.APPLIANCE]: {
        render({extraHandler, isQFP, ...props}) {
            return <Appliance {...props} dropdown={Dropdown} />;
        },
    },
    [FIELD_TYPES.DOOR_HANG]: {
        render(options) {
            if (!options.isQFP) {
                options.selectHandler = (name, value) => {
                    if (value != 2) {
                        // if value is not none
                        if (
                            !options.values.hasOwnProperty('drillings') ||
                            (options.values.hasOwnProperty('drillings') &&
                                options.values.drillings.length == 0)
                        ) {
                            const drillings = [
                                {
                                    drilling_offset_y:
                                        options.defaultHingePosition,
                                },
                                {
                                    drilling_offset_y:
                                        options.defaultHingePosition,
                                },
                            ];

                            options.setFieldValue('drillings', drillings);
                        }
                    } else {
                        options.setFieldValue('drillings', []);
                    }

                    options.setFieldValue(name, value);
                };
            }

            return Dropdown.render(options);
        },
    },
    [FIELD_TYPES.SHELVES_THICKNESS]: {
        render(options) {
            return <ShelfThickness />;
        },
    },
};

export const Field = ({
    field,
    fieldsetName,
    dataSource = false,
    fieldSpan = 6,
    formControlSpan = 2,
    selectHandler = false,
    extraHandler = false,
    indexedValues = false,
    isQFP = false,
    fieldSetIndex,
    placeholder = '',
    isStackFields = false,
    cancelButton = null,
    buttonSlot = null,
    fieldHeaderName = null,
}) => {
    const {values, setFieldValue, errors} = useFormikContext();
    const {
        currentTab,
        productDataStore,
        getProductInfo,
        getInitialValue,
        getMaterialOptions,
        forty5Fields,
        showDialog,
        hideDialog,
        setCurrentTab,
        formStructure,
        defaultValuesReference,
        productDefaultsReference,
        fieldIndex,
        validationData,
        isMiniBrowser,
    } = useProductContext();

    const [fetchFingerPulStyles] = useLazyGetFingerPullStylesQuery();

    const {data: drawerSystems} = useGetDrawerSystemQuery();
    const {data: hingeStyles} = useGetHingeStyle(values.cabinet_type);
    const {userProfile} = useAppContext();
    const {messages} = useNotificationContext();

    const [dataSourceParams, setDataSourceParams] = useState();
    const isSmallDevice = isDeviceSmall();
    const isTabletSize = useTabletSize();

    const [fieldName, fieldType] = useMemo(() => {
        return [field.name, field.type];
    }, [field]);

    const isInvalid = useMemo(() => {
        let errorMessages = messages.errors;

        if (
            isQFP &&
            fieldIndex > -1 &&
            errorMessages.hasOwnProperty(fieldIndex)
        ) {
            // eslint-disable-next-line security/detect-object-injection -- Checked in the if statement above
            errorMessages = errorMessages[fieldIndex];
        }

        if (!Array.isArray(errorMessages) || errorMessages.length == 0) {
            return false;
        }

        if (errors && ['door_hinge_amount_1'].includes(fieldName)) {
            errorMessages = errorMessages?.filter((e) =>
                e.fields?.some((f) => Object.keys(errors).includes(f))
            );
        }

        return errorMessages.some((error) =>
            validationErrorAppliesToField(error, fieldName, fieldSetIndex)
        );
    }, [messages, isQFP, fieldIndex, errors, fieldName, fieldSetIndex]);

    const fieldLabel = useMemo(() => {
        let fieldLabel =
            field.hasOwnProperty('title') && field.title
                ? field.title
                : field.hasOwnProperty('displayName') && field.displayName
                  ? field.displayName
                  : false;

        if (
            fieldLabel &&
            fieldLabel.indexOf('{') > -1 &&
            fieldLabel.indexOf('}') > -1
        ) {
            fieldLabel = getFieldValue(fieldLabel, values, userProfile);
        }

        return fieldLabel;
    }, [field, values, userProfile]);

    const [formikField] = useField({name: field.name});

    const fieldValue = useMemo(() => {
        let fieldValue = indexedValues
            ? indexedValues[field.name]
            : formikField.value;

        if (fieldType === FIELD_TYPES.SIZE_LABEL) {
            if (typeof field.value === 'string') {
                fieldValue = field.value;
            }
            fieldValue = getFieldValue(
                fieldValue,
                {...values, ...getMaterialOptions()},
                userProfile
            );
        }

        if (fieldType === FIELD_TYPES.LABEL) {
            if (
                field.value &&
                field.value.indexOf('{') > -1 &&
                field.value.indexOf('}') > -1
            ) {
                fieldValue = getFieldValue(
                    field.value,
                    {...values, ...getMaterialOptions()},
                    userProfile
                );
            } else fieldValue = field.value;
        }

        if (
            field.name &&
            (field.name.indexOf('hori_mid_rail_positions') > -1 ||
                field.name.indexOf('vert_mid_rail_positions') > -1)
        ) {
            return typeof fieldValue == 'undefined' ? '' : fieldValue;
        }
        fieldValue = typeof fieldValue === 'undefined' ? 0 : fieldValue;

        if (fieldValue === null && fieldType === FIELD_TYPES.SIZE_PERCENTAGE) {
            fieldValue = 0;
        }

        return fieldValue;
    }, [formikField, field, indexedValues, values, userProfile]);

    if (
        !forty5Fields.current.hasOwnProperty(fieldName) &&
        field.hasOwnProperty('options') &&
        field.options.hasOwnProperty('needs45') &&
        field.options.needs45
    ) {
        forty5Fields.current[String(fieldName)] = fieldValue;
    }

    const currentDoor = useGetDoorQuery(
        {
            keywords: values.cabinet_door,
            cabinet_type: values.cabinet_type,
            manufacturerId: userProfile?.manufacturerId,
        },
        true
    );

    const visible = useMemo(() => {
        const valueContext = cloneDeep(values);

        valueContext.cabinet_door = {
            advanced: currentDoor.isLoading
                ? false
                : currentDoor?.data?.data?.is_advanced,
        };

        return getVisibility(
            field,
            fieldValue,
            valueContext,
            indexedValues,
            getMaterialOptions(),
            fieldSetIndex
        );
    }, [
        field,
        fieldValue,
        values,
        indexedValues,
        fieldSetIndex,
        currentDoor.isLoading,
    ]);

    const enabled = useMemo(() => {
        let enabled = true;

        if (hasOption(field, 'enabled')) {
            const controlledEnabled = field.options.enabled;

            if (typeof controlledEnabled === 'function') {
                enabled = controlledEnabled(values, fieldValue, indexedValues);
            } else {
                if (isOptionBoolean(field, 'enabled')) {
                    enabled = controlledEnabled;
                } else {
                    try {
                        enabled = isFieldEnabled(
                            field,
                            values,
                            getMaterialOptions(),
                            fieldSetIndex
                        );
                    } catch (e) {
                        // unable to calculate field enabled
                        // report to apm if needed
                    }
                }
            }
        }

        return enabled;
    }, [values, indexedValues, validationData, field, fieldValue]);

    const fieldStyles = useMemo(() => {
        const isDrawerEdges =
            fieldsetName === FIELDSET_LAYOUT.DRAWER_EDGES && isQFP;
        return {
            colStyle: {
                height: isDrawerEdges ? '40px' : null,
            },
            labelStyle: {
                paddingLeft: isDrawerEdges ? 30 : null,
            },
        };
    }, [fieldsetName]);

    useEffect(() => {
        if (!enabled && hasOption(field, 'forceDefaultValueWhenDisabled')) {
            try {
                const calculatedValue = excel.calculate(
                    getInitialValue(fieldName),
                    values
                );

                const isVisible = getVisibility(
                    field,
                    fieldValue,
                    values,
                    indexedValues,
                    getMaterialOptions()
                );

                if (values[String(fieldName)] != calculatedValue && isVisible) {
                    if (isQFP && typeof selectHandler === 'function') {
                        selectHandler(fieldName, calculatedValue);
                    } else {
                        setFieldValue(fieldName, calculatedValue);
                    }
                }
            } catch (e) {
                // unable to calculate default value,
                // report to apm later
            }
        }
    }, [enabled]);

    useEffect(() => {
        try {
            if (
                field.hasOwnProperty('options') &&
                field.options.hasOwnProperty('needs45') &&
                field.options.needs45
            ) {
                if (
                    forty5Fields.current.cabinet_left_width !=
                    values.cabinet_left_width
                ) {
                    if (fieldName === 'cabinet_left_width') {
                        forty5Fields.current.cabinet_left_width =
                            values.cabinet_left_width;

                        let newLeftWidth;
                        if (
                            values.cabinet_left_width <=
                            values.cabinet_right_depth
                        ) {
                            newLeftWidth = excel.calculate(
                                'cabinet_right_depth + 1',
                                values
                            );
                            forty5Fields.current.cabinet_left_width =
                                newLeftWidth;
                        }

                        let newLeftDepth;
                        let newRightDepth = excel.calculate(
                            'cabinet_left_width - cabinet_right_width + cabinet_left_depth',
                            forty5Fields.current
                        );

                        if (newRightDepth <= 0) {
                            newLeftDepth = excel.calculate(
                                'cabinet_right_width - cabinet_left_width + 1',
                                forty5Fields.current
                            );
                            newRightDepth = 1;
                        }

                        setFieldValue('cabinet_right_depth', newRightDepth);
                        forty5Fields.current['cabinet_right_depth'] =
                            newRightDepth;
                        forty5Fields.current.widthChanged = true;

                        if (newLeftDepth) {
                            forty5Fields.current['cabinet_left_depth'] =
                                newLeftDepth;
                            setFieldValue('cabinet_left_depth', newLeftDepth);
                        }

                        if (newLeftWidth) {
                            setFieldValue('cabinet_left_width', newLeftWidth);
                        }
                    }
                }

                if (
                    forty5Fields.current.cabinet_right_width !=
                    values.cabinet_right_width
                ) {
                    if (fieldName === 'cabinet_right_width') {
                        forty5Fields.current.cabinet_right_width =
                            values.cabinet_right_width;
                        let newRightWidth;
                        if (
                            values.cabinet_right_width <=
                            values.cabinet_left_depth
                        ) {
                            newRightWidth = excel.calculate(
                                'cabinet_left_depth + 1',
                                values
                            );
                            forty5Fields.current.cabinet_right_width =
                                newRightWidth;
                        }

                        let newRightDepth;
                        let newLeftDepth = excel.calculate(
                            'cabinet_right_width - cabinet_left_width + cabinet_right_depth',
                            forty5Fields.current
                        );

                        if (newLeftDepth <= 0) {
                            newRightDepth = excel.calculate(
                                'cabinet_left_width - cabinet_right_width + 1',
                                forty5Fields.current
                            );
                            newLeftDepth = 1;
                        }

                        setFieldValue('cabinet_left_depth', newLeftDepth);
                        forty5Fields.current['cabinet_left_depth'] =
                            newLeftDepth;
                        forty5Fields.current.widthChanged = true;

                        if (newRightDepth) {
                            setFieldValue('cabinet_right_depth', newRightDepth);
                            forty5Fields.current['cabinet_right_depth'] =
                                newRightDepth;
                        }

                        if (newRightWidth) {
                            setFieldValue('cabinet_right_width', newRightWidth);
                        }
                    }
                }

                if (
                    forty5Fields.current.cabinet_left_depth !=
                    values.cabinet_left_depth
                ) {
                    if (fieldName === 'cabinet_left_depth') {
                        forty5Fields.current.cabinet_left_depth =
                            values.cabinet_left_depth;
                        const leftFace = excel.calculate(
                            'cabinet_right_width - cabinet_left_depth',
                            forty5Fields.current
                        );
                        const rightFace = excel.calculate(
                            'cabinet_left_width - cabinet_right_depth',
                            forty5Fields.current
                        );

                        if (
                            leftFace != rightFace &&
                            !forty5Fields.current.widthChanged
                        ) {
                            let newLeftDepth;

                            if (
                                values['cabinet_left_depth'] >
                                values['cabinet_right_width']
                            ) {
                                newLeftDepth = excel.calculate(
                                    'cabinet_right_width - 1',
                                    values
                                );
                                forty5Fields.current.cabinet_left_depth =
                                    newLeftDepth;
                            }

                            let newRightDepth = excel.calculate(
                                'cabinet_left_width - cabinet_right_width + cabinet_left_depth',
                                forty5Fields.current
                            );

                            if (newRightDepth <= 0) {
                                newLeftDepth = excel.calculate(
                                    'cabinet_right_width - cabinet_left_width + 1',
                                    forty5Fields.current
                                );
                                newRightDepth = 1;
                            }

                            setFieldValue('cabinet_right_depth', newRightDepth);
                            forty5Fields.current['cabinet_right_depth'] =
                                newRightDepth;

                            if (newLeftDepth) {
                                forty5Fields.current['cabinet_left_depth'] =
                                    newLeftDepth;
                                setFieldValue(
                                    'cabinet_left_depth',
                                    newLeftDepth
                                );
                            }
                        }

                        forty5Fields.current.widthChanged = false;
                    }
                }

                if (
                    forty5Fields.current.cabinet_right_depth !=
                    values.cabinet_right_depth
                ) {
                    if (fieldName === 'cabinet_right_depth') {
                        forty5Fields.current.cabinet_right_depth =
                            values.cabinet_right_depth;
                        const leftFace = excel.calculate(
                            'cabinet_right_width - cabinet_left_depth',
                            forty5Fields.current
                        );
                        const rightFace = excel.calculate(
                            'cabinet_left_width - cabinet_right_depth',
                            forty5Fields.current
                        );

                        if (
                            leftFace != rightFace &&
                            !forty5Fields.current.widthChanged
                        ) {
                            let newRightDepth;

                            if (
                                values['cabinet_right_depth'] >
                                values['cabinet_left_width']
                            ) {
                                newRightDepth = excel.calculate(
                                    'cabinet_left_width - 1',
                                    values
                                );
                                forty5Fields.current.cabinet_right_depth =
                                    newRightDepth;
                            }

                            let newLeftDepth = excel.calculate(
                                'cabinet_right_width - cabinet_left_width + cabinet_right_depth',
                                forty5Fields.current
                            );

                            if (newLeftDepth <= 0) {
                                newRightDepth = excel.calculate(
                                    'cabinet_left_width - cabinet_right_width + 1',
                                    forty5Fields.current
                                );
                                newLeftDepth = 1;
                            }

                            setFieldValue('cabinet_left_depth', newLeftDepth);
                            forty5Fields.current['cabinet_left_depth'] =
                                newLeftDepth;

                            if (newRightDepth) {
                                setFieldValue(
                                    'cabinet_right_depth',
                                    newRightDepth
                                );
                                forty5Fields.current['cabinet_right_depth'] =
                                    newRightDepth;
                            }
                        }

                        forty5Fields.current.widthChanged = false;
                    }
                }
            }
        } catch (e) {
            // eslint-disable-next-line no-console -- simply printing out the error if any
            console.log(e);
        }
        // NOTE: Move this to separate hook helper
    }, [values, field]);

    const paramsEffectModifiers = isQFP
        ? [values.edge_colour, values.cabinet_include_hardware]
        : [false, false, false, false];

    useEffect(() => {
        switch (field.type) {
            case FIELD_TYPES.PART_TOP:
                let availableTops = getProductInfo('available_cabinet_tops');
                availableTops = availableTops
                    ? JSON.parse(`[${availableTops}]`)
                    : undefined;

                const showNone =
                    productDataStore.current.hasOwnProperty(
                        'has_none_in_tops'
                    ) && productDataStore.current.has_none_in_tops;

                setDataSourceParams({availableTops, showNone});
                break;

            case FIELD_TYPES.HINGE_STYLE:
                setDataSourceParams({
                    hingeStyles,
                    include_hardware: values.cabinet_include_hardware,
                    isAssemblyOnly: userProfile.isAssemblyOnly,
                });
                break;

            case FIELD_TYPES.FINGER_PULL:
                setDataSourceParams({
                    defaultValuesReference,
                    productDefaultsReference,
                    fetchFingerPulStyles,
                });
                break;
        }
    }, [
        ...paramsEffectModifiers,
        values.cabinet_include_hardware,
        hingeStyles,
        userProfile,
    ]);

    const inSpecsTab = currentTab === 0 && !isQFP;

    const memoModifiers = [
        fieldValue,
        selectHandler ? selectHandler : 0,
        isInvalid,
        dataSource ? dataSource : 0,
        dataSourceParams ? dataSourceParams : 0,
        enabled,
        extraHandler ? extraHandler : 0,
        isQFP,
        values,
        inSpecsTab,
    ];

    const randomId = useRef(randomString(5)).current;
    const renderedField = useMemo(() => {
        let formField = FieldStrategy[String(fieldType)];
        const renderOptions = {
            name: fieldName,
            value: fieldValue,
            setFieldValue,
            selectHandler,
            isInvalid,
            field,
            dataSource,
            dataSourceParams,
            values,
            enabled,
            extraHandler,
            isQFP,
            showDialog,
            hideDialog,
            setCurrentTab,
            formStructure,
            randomId,
            fieldSetIndex,
            placeholder,
            fieldIndex,
            inSpecsTab,
            productDataStore,
        };

        if (
            fieldType == FIELD_TYPES.SELECT &&
            fieldName == FIELD_TYPES.DOOR_HANG
        ) {
            renderOptions.defaultHingePosition =
                userProfile?.manufacturerDefaultHingePosition;

            formField = FieldStrategy[FIELD_TYPES.DOOR_HANG];
        }

        if (typeof formField === 'undefined') {
            formField = FieldStrategy[FIELD_TYPES.TEXT];
        }

        if (
            fieldType === FIELD_TYPES.SIZE_PERCENTAGE ||
            fieldType === FIELD_TYPES.EDGE_FINISH
        ) {
            renderOptions.fieldsetName = fieldsetName;
        }

        if (fieldType === FIELD_TYPES.HARDWARE_METHOD) {
            renderOptions.drawerSystems = drawerSystems;
            renderOptions.hingeStyles = hingeStyles;
        }

        renderOptions.fieldSetGroupName = fieldsetName;

        return formField.render(renderOptions);
    }, memoModifiers);

    if (!visible) return null;

    const ButtonSlotContainer = !!buttonSlot
        ? ButtonSlotWrapper
        : React.Fragment;

    return (
        <Row className={fieldName}>
            {field.type === FIELD_TYPES.LABEL &&
            field.displayName == null &&
            (!field.options.hasOwnProperty('style') ||
                field.options.style != 'underneath_field') ? (
                // eslint-disable-next-line react/jsx-indent -- can't really win here
                <Col>
                    {FieldStrategy[FIELD_TYPES.LABEL].render({
                        name: fieldName,
                        value: fieldValue,
                        style: field.options.hasOwnProperty('style')
                            ? field.options.style
                            : 'light',
                    })}
                </Col>
            ) : (
                <>
                    {fieldHeaderName}
                    <Col
                        style={fieldStyles.colStyle}
                        data-cy={`${fieldName}`}
                        xs={
                            inSpecsTab && isTabletSize
                                ? fieldSpan === 8
                                    ? 12
                                    : fieldSpan
                                : fieldSpan
                        }>
                        {cancelButton}
                        {fieldType === FIELD_TYPES.APPLIANCE ? (
                            renderedField
                        ) : (
                            <FormGroup
                                controlId={`${field.name}_${randomId}`}
                                className={
                                    field.type == FIELD_TYPES.TEXT &&
                                    field.options.hasOwnProperty('multiLine') &&
                                    field.options.multiLine
                                        ? 'is-text-area'
                                        : ''
                                }
                                asRow={
                                    ![
                                        'cabinet_include_assembly',
                                        'cabinet_include_hardware',
                                        'include_drawer_faces',
                                        'cabinet_adjustable_legs',
                                    ].includes(field.name)
                                }
                                style={
                                    ![
                                        'cabinet_include_assembly',
                                        'cabinet_include_hardware',
                                        'include_drawer_faces',
                                        'cabinet_adjustable_legs',
                                    ].includes(field.name)
                                        ? {}
                                        : {flexDirection: 'column'}
                                }>
                                <Form.Label
                                    style={fieldStyles.labelStyle}
                                    column
                                    xs={
                                        // a fieldset that asked to stack
                                        // wants its label on its own line
                                        isStackFields
                                            ? 12
                                            : isMiniBrowser
                                              ? 6
                                              : inSpecsTab
                                                ? isSmallDevice
                                                    ? undefined
                                                    : 4
                                                : formControlSpan
                                    }>
                                    {fieldLabel
                                        ? `${fieldLabel}${isStackFields ? '' : ':'}\u00A0`
                                        : null}
                                    {field.hasOwnProperty('options') &&
                                    field.options.hasOwnProperty('mandatory') &&
                                    field.options.mandatory ? (
                                        <span>*</span>
                                    ) : null}

                                    {field.hasOwnProperty('options') &&
                                    field.options.hasOwnProperty('moreInfo') &&
                                    field.options.moreInfo ? (
                                        <MoreInfo
                                            info={field.options.moreInfo}
                                            scope={
                                                productDataStore.current &&
                                                productDataStore.current
                                                    .cabinet &&
                                                productDataStore.current.cabinet
                                                    .attributes
                                                    ? productDataStore.current
                                                          .cabinet.attributes
                                                    : {}
                                            }
                                            styleOverride={
                                                isQFP
                                                    ? {
                                                          top: '7px',
                                                      }
                                                    : {}
                                            }
                                        />
                                    ) : null}
                                </Form.Label>
                                <Col
                                    xs={
                                        isStackFields
                                            ? 12
                                            : isMiniBrowser
                                              ? 6
                                              : inSpecsTab
                                                ? isSmallDevice
                                                    ? 'auto'
                                                    : 8
                                                : 12 - formControlSpan
                                    }>
                                    <ButtonSlotContainer>
                                        {field.type === FIELD_TYPES.LABEL ? (
                                            <FormGroup
                                                style={
                                                    !field.options.hasOwnProperty(
                                                        'style'
                                                    ) ||
                                                    field.options.style !=
                                                        'underneath_field'
                                                        ? {margin: '5px 0 0 0'}
                                                        : {}
                                                }>
                                                {FieldStrategy[
                                                    FIELD_TYPES.LABEL
                                                ].render({
                                                    name: fieldName,
                                                    value: fieldValue,
                                                    style: field.options.hasOwnProperty(
                                                        'style'
                                                    )
                                                        ? field.options.style
                                                        : 'form_item',
                                                })}
                                            </FormGroup>
                                        ) : (
                                            renderedField
                                        )}
                                        {buttonSlot}
                                    </ButtonSlotContainer>
                                </Col>
                            </FormGroup>
                        )}
                    </Col>
                </>
            )}
        </Row>
    );
};
